import { isRoutedOwnerPage } from "@stackje/site-builder/bundle-pages";
import type { ActionState, ActionSummary, PublicationReview, SiteSummary, Workspace, WorkspacePage } from "../api/contract";

export const TERMINAL_STATES: ReadonlySet<ActionState> = new Set(["succeeded", "conflict", "failed", "cancelled"]);
export const IN_FLIGHT_STATES: ReadonlySet<ActionState> = new Set(["queued", "running"]);

export const isTerminal = (action: Pick<ActionSummary, "state">) => TERMINAL_STATES.has(action.state);
export const isInFlight = (action: Pick<ActionSummary, "state">) => IN_FLIGHT_STATES.has(action.state);

export function newActionId(): string {
  return `action-${crypto.randomUUID()}`;
}

/**
 * A preview root is a path prefix under which a rendered site keeps all its links: any saved draft,
 * addressed by id. Used both for History's earlier versions and for the Before side of a redesign
 * comparison card (the action's `baseDraftId`).
 */
export function draftPreviewRoot(siteId: string, draftId: string): string {
  return `/_builder/preview/${encodeURIComponent(siteId)}/${encodeURIComponent(draftId)}`;
}

/** The publish review: a draft exactly as it will go live, with empty slots left out. */
export function releasePreviewRoot(siteId: string, draftId: string): string {
  return `/_builder/release-preview/${encodeURIComponent(siteId)}/${encodeURIComponent(draftId)}`;
}

export function urlInRoot(root: string, path = "/"): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${root}${normalized}`;
}

/** Maps a location inside the preview frame back to a site path, or null when it has left the root. */
export function pathInRoot(root: string, pathname: string): string | null {
  if (pathname === root || pathname === `${root}/`) return "/";
  if (!pathname.startsWith(`${root}/`)) return null;
  return pathname.slice(root.length);
}

/** Draft-pinned private preview URL. Navigation inside the rendered site stays on this draft. */
export function previewUrl(siteId: string, draftId: string, path = "/"): string {
  return urlInRoot(draftPreviewRoot(siteId, draftId), path);
}

export function sitePathFromPreview(siteId: string, draftId: string, pathname: string): string | null {
  return pathInRoot(draftPreviewRoot(siteId, draftId), pathname);
}

export function isPublication(action: ActionSummary): action is ActionSummary & { review: PublicationReview } {
  return action.review !== null;
}

/**
 * A prepared publication may be confirmed only while the site still matches what was reviewed.
 * The server enforces this too; the client check avoids offering a confirmation that must fail.
 */
export function isReviewStale(review: PublicationReview, site: SiteSummary): boolean {
  if (review.expectedServingEpoch !== site.servingEpoch) return true;
  return review.kind === "publish" && review.draftId !== site.draftId;
}

/** Actions arrive most-recent first. */
export function latestPublication(workspace: Workspace, predicate: (action: ActionSummary) => boolean): ActionSummary | null {
  return workspace.actions.find((action) => isPublication(action) && action.review.kind === "publish" && predicate(action)) ?? null;
}

/** An in-flight publication to keep following after reload or reconnect. Following never confirms anything. */
export function inFlightPublication(workspace: Workspace): ActionSummary | null {
  return latestPublication(workspace, isInFlight);
}

/** A prepared review the owner left open. It is offered again, never confirmed automatically. */
export function waitingReview(workspace: Workspace): ActionSummary | null {
  const action = latestPublication(workspace, (candidate) => candidate.state === "awaiting-confirmation");
  if (!action?.review || isReviewStale(action.review, workspace.site)) return null;
  return action;
}

export type LiveStatus =
  | { kind: "never-published" }
  | { kind: "matches-draft" }
  | { kind: "has-changes" }
  /** Live, but the release that is serving is older than the retained history, so we cannot compare it. */
  | { kind: "live" };

export function liveStatus(workspace: Workspace): LiveStatus {
  const { site } = workspace;
  if (!site.activeReleaseId) return { kind: "never-published" };
  const serving = workspace.actions.find(
    (action) => action.state === "succeeded" && action.kind === "publish" && action.result?.releaseId === site.activeReleaseId,
  );
  const servingDraft = serving?.result?.draftId ?? serving?.review?.draftId;
  if (typeof servingDraft !== "string") return { kind: "live" };
  return servingDraft === site.draftId ? { kind: "matches-draft" } : { kind: "has-changes" };
}

/** The page a preview opens on: the root when the draft has one, otherwise its first page. */
export function startPath(pages: Pick<WorkspacePage, "path" | "template" | "special">[]): string {
  const routed = pages.filter(isRoutedOwnerPage);
  if (routed.some((page) => page.path === "/")) return "/";
  return routed[0]?.path ?? "/";
}

/** Publication needs a root page; preview does not. */
export function hasHomePage(pages: Pick<WorkspacePage, "path">[]): boolean {
  return pages.some((page) => page.path === "/");
}

/** The public address of the site, without protocol, for display. */
export function displayOrigin(canonicalOrigin: string): string {
  try {
    return new URL(canonicalOrigin).host;
  } catch {
    return canonicalOrigin;
  }
}
